import { Button, Sheet, Stack } from "@mui/joy";
import { useCallback, useEffect, useState } from "react";
import { taskApi } from "api";
import { useMutation } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { ProgramContentType } from "data/BlockComponent";
import PlainTextarea from "./PlainTextarea";
import { ElementType } from "data/CellComponent";
import { debounce } from "lodash";
import * as _ from "lodash";
import { useTranslation } from "react-i18next";

export default function Feedback({
  taskKey,
  savedFeedback,
  index,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
}: {
  taskKey: string;
  savedFeedback?: string;
  index: number;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
}) {
  const userState = useRecoilValue(userAtom);
  const isCoach = userState?.role !== "patient";
  const [feedback, setFeedback] = useState<string>(savedFeedback || "");
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  const submitFeedbackQueryFn = useCallback(
    () => taskApi.submitFeedback(taskKey, feedback),
    [taskKey, feedback]
  );

  const { mutate: submitFeedback } = useMutation(submitFeedbackQueryFn, {
    onSuccess: () => {
      setFeedback("");
      handleChange("", true);
    },
  });

  const handleChangeTemp = (value: string) => {
    setFeedback(value);
  };
  const handleChange = (value: string, autoOpenNext?: boolean) => {
    setData((data) => {
      if (data && data[index].lines) {
        const originalCurrentContent = (data[index].lines[lineIndex][cellIndex]
          .content || {}) as ElementType;
        const originalValue = originalCurrentContent.value || "";

        if (!(originalValue === value)) {
          const data_temp = _.cloneDeep(data);
          const currentContent =
            data_temp[index].lines[lineIndex][cellIndex].content || {};

          data_temp[index].lines[lineIndex][cellIndex].content = {
            ...currentContent,
            value: value,
            htmlString: `<p>${value}</p>`,
          };
          if (autoOpenNext) {
            data_temp[index].autoOpenNext = true;
          }

          return data_temp;
        }
        return data;
      }
      return data;
    });
  };

  const debouncedSaveText = debounce(handleChange, 500);

  useEffect(() => {
    debouncedSaveText(feedback);

    // 컴포넌트가 언마운트될 때 디바운스 함수를 취소합니다.
    return () => debouncedSaveText.cancel();
  }, [feedback, debouncedSaveText]);

  return (
    <Stack direction={"row"} spacing={1}>
      <div className={`w-full`}>
        <Sheet
          sx={{
            position: "relative",
            height: "100%",
            backgroundColor: "transparent",
            borderRadius: "4px",
            borderWidth: 1,
            borderColor: "primary.deactivated",
            px: "12px",
            py: "5px",
            color: "text.positive",
            outlineWidth: "10px",
            outlineColor: "black",
          }}
        >
          {isFocused && (
            <Sheet
              className="w-[calc(100%+2px)] h-[calc(100%+2px)] top-[-1px] left-[-1px]"
              sx={{
                backgroundColor: "transparent",
                position: "absolute",
                borderWidth: "var(--joy-focus-thickness, 2)",
                borderRadius: "4px",
                borderColor: "primary.solid",
              }}
            />
          )}

          <PlainTextarea
            taskKey={taskKey}
            placeholder={t_common("소감 placeholder")}
            initialValue={savedFeedback}
            handleChange={handleChangeTemp}
            index={index}
            isFocused={isFocused}
            setIsFocused={setIsFocused}
            editorKey={editorKey}
          />
        </Sheet>
      </div>

      <Button
        disabled={isCoach || feedback?.length === 0}
        sx={{ px: "10px" }}
        onClick={() => {
          submitFeedback();
        }}
      >
        <span className="material-symbols-rounded">send</span>
      </Button>
    </Stack>
  );
}
